// frontend/src/pages/owner/TenantsList.jsx
import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Button, Card, CardContent, Typography, Stack, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import http from '../../services/http';
import OwnerBrandFooter from '../../components/owner/OwnerBrandFooter';

export default function TenantsList() {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState('');
  const [confirm, setConfirm] = useState({ open: false, slug: '', name: '' });
  const [busy, setBusy] = useState(false);
  const [warn, setWarn] = useState(null); // { users, visits } when 409

  // Optional: if Owner portal is hosted on a different origin than CMFM portal,
  // set REACT_APP_CMFM_ORIGIN (e.g., https://cmfm.mededtek.in)
  const CMFM_ORIGIN = (process.env.REACT_APP_CMFM_ORIGIN || '').replace(/\/+$/g, '');

  const buildCmfmLoginUrl = (slug) => {
    const s = encodeURIComponent(String(slug || '').trim());
    const path = `/t/${s}/cmfm/login`;
    return CMFM_ORIGIN ? `${CMFM_ORIGIN}${path}` : path;
  };

  const load = useCallback(async () => {
    try {
      setErr('');
      const { data } = await http.get('/api/owner/tenants');
      setRows(data.tenants || []);
    } catch (e) {
      setErr(e?.response?.data?.message || e?.message || 'Failed to load tenants');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const askDelete = (t) => {
    setWarn(null);
    setConfirm({ open: true, slug: t.slug, name: t.name || t.slug });
  };

  const closeDialog = () => {
    if (busy) return;
    setConfirm({ open: false, slug: '', name: '' });
    setWarn(null);
  };

  const doDelete = async (force = false) => {
    setBusy(true);
    try {
      const url = `/api/owner/tenants/${encodeURIComponent(confirm.slug)}${force ? '?force=true' : ''}`;
      await http.delete(url);
      closeDialog();
      await load();
    } catch (e) {
      const status = e?.response?.status;
      if (status === 409) {
        const data = e?.response?.data || {};
        setWarn({ users: data.users || 0, visits: data.visits || 0 });
      } else {
        alert(e?.response?.data?.message || e?.message || 'Delete failed');
        closeDialog();
      }
    } finally {
      setBusy(false);
    }
  };

  if (err) return <Box p={3}><Typography color="error">{err}</Typography></Box>;
  if (!rows) return <Box p={3}><CircularProgress size={24} /></Box>;

  return (
    <Box p={3}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5">Institutional Portals</Typography>
        <Button component={RouterLink} to="/owner/tenants/new" variant="contained">New Institutional Portal</Button>
      </Stack>

      <Stack spacing={2}>
        {rows.map(t => (
          <Card key={t._id || t.slug}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                <Box>
                  <Typography variant="h6">{t.name}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t.slug} • {t.dept || 'Dept'}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Typography variant="body2">Users: {t.users}</Typography>
                  <Typography variant="body2">Visits: {t.visits}</Typography>
                  <Button component={RouterLink} to={`/owner/tenants/${t.slug}`} size="small" variant="outlined">Open</Button>
                  <Button
                    size="small"
                    variant="contained"
                    href={buildCmfmLoginUrl(t.slug)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Open Portal
                  </Button>
                  <Button
                    size="small"
                    color="error"
                    variant="outlined"
                    onClick={() => askDelete(t)}
                  >
                    Delete
                  </Button>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        ))}
      </Stack>

      <Dialog open={confirm.open} onClose={closeDialog} fullWidth maxWidth="sm">
        <DialogTitle>Delete Portal “{confirm.name}”?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mt: 1 }}>
            This will remove the institutional portal <b>{confirm.slug}</b>.
          </Typography>
          {warn ? (
            <Typography variant="body2" sx={{ mt: 1 }}>
              This portal still has <b>{warn.users}</b> users and <b>{warn.visits}</b> visits.
              You can proceed with a <b>Force Delete</b> to cascade-delete them.
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              This action cannot be undone.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={busy}>Cancel</Button>
          {!warn && (
            <Button onClick={() => doDelete(false)} color="error" variant="contained" disabled={busy}>
              {busy ? 'Deleting…' : 'Delete'}
            </Button>
          )}
          {warn && (
            <Button onClick={() => doDelete(true)} color="error" variant="contained" disabled={busy}>
              {busy ? 'Deleting…' : 'Force Delete'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
      <OwnerBrandFooter />
    </Box>
  );
}
